import React from 'react';
import { BiX } from 'react-icons/bi';

interface LoanDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerData: {
    userId: string;
    email: string;
    fullname: string;
    phoneNumber: string;
    bvn: string;
    employment: string;
    loanAmount: number;
    eligibleAmount: number;
    amountLeft: number;
    amountToBePaid: number;
    nextPayDate: string;
    cycle: number;
    dailyInterest: number;
    dailyInterestIncrement: number;
    loanStatus: string;
    walletBalance: number;
  };
}

const LoanDashboardModal: React.FC<LoanDashboardModalProps> = ({
  isOpen,
  onClose,
  customerData,
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateLoanPeriod = () => {
    const nextPayDate = new Date(customerData.nextPayDate);
    const currentDate = new Date();
    const diffTime = nextPayDate.getTime() - currentDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateDueDate = () => {
    const nextPayDate = new Date(customerData.nextPayDate);
    return nextPayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const totalOwedToday = customerData.amountToBePaid;
  const loanableAmount = customerData.amountLeft;
  const loanPeriod = calculateLoanPeriod();
  const dailyInterest = customerData.dailyInterest;

  // Calculate loan breakdown
  const unpaidPrincipal = customerData.loanAmount - (customerData.loanAmount - customerData.amountLeft);
  const unpaidInterest = (customerData.amountToBePaid - unpaidPrincipal) * 0.3; // Approximate
  const unpaidServiceCharge = (customerData.amountToBePaid - unpaidPrincipal) * 0.7; // Approximate
  const overDueManagementFee = 0;
  const deliveryFee = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {customerData.fullname}'s Dashboard - Loans
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <BiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Loans Card */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-6 text-white shadow-lg">
            <h3 className="text-sm font-medium mb-4 opacity-90">Loans</h3>
            
            <div className="mb-2">
              <p className="text-xs opacity-80 mb-1">Total amount owed today</p>
              <p className="text-3xl font-bold">{formatCurrency(totalOwedToday)}</p>
            </div>
            
            <div className="h-px bg-white opacity-30 my-4"></div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs opacity-80 mb-1">Loanable Amount</p>
                <p className="text-xl font-bold">{formatCurrency(loanableAmount)}</p>
              </div>
              <div>
                <p className="text-xs opacity-80 mb-1">Loan Period</p>
                <p className="text-xl font-bold">{loanPeriod} <span className="text-sm">Days</span></p>
              </div>
            </div>
            
            <div className="mt-3">
              <p className="text-xs opacity-80">Daily Interest: <span className="text-red-400 font-semibold">{dailyInterest}</span></p>
            </div>
          </div>

          {/* Outstanding */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Outstanding</p>
            <p className="text-sm text-gray-600">{customerData.cycle} terms left</p>
          </div>

          <div className="text-3xl font-bold text-gray-900 mb-6">
            {formatCurrency(customerData.loanAmount)}
          </div>

          {/* Loan Overview */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Loan Overview</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unpaid Principal</span>
                <span className="font-medium text-gray-900">{formatCurrency(unpaidPrincipal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unpaid Interest</span>
                <span className="font-medium text-gray-900">{formatCurrency(unpaidInterest)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unpaid Service Charge</span>
                <span className="font-medium text-gray-900">{formatCurrency(unpaidServiceCharge)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Over Due Management Fee</span>
                <span className="font-medium text-gray-900">{formatCurrency(overDueManagementFee)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Delivery Fee</span>
                <span className="font-medium text-gray-900">{formatCurrency(deliveryFee)}</span>
              </div>
            </div>
          </div>

          {/* Loan Amount and Repaid Amount */}
          <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <span className="text-sm font-medium text-gray-700">Loan Amount:</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{formatCurrency(customerData.loanAmount)}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <button className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <span className="text-sm font-medium text-gray-700">Repaid Amount:</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{formatCurrency(customerData.loanAmount - customerData.amountLeft)}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>

          {/* Terms */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Terms</h4>
            
            {/* 1st Term */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-semibold">1/2</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">1st Term</p>
                <p className="text-xs text-gray-500">Due {calculateDueDate()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(customerData.loanAmount / 2)}
                </p>
              </div>
            </div>

            {/* 2nd Term */}
            {customerData.cycle === 2 && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-semibold">1/2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">2nd Term</p>
                  <p className="text-xs text-gray-500">Due {calculateDueDate()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(customerData.loanAmount / 2)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="pt-4 border-t border-gray-200 space-y-2 text-xs text-gray-600">
            <div className="flex justify-between">
              <span>Email:</span>
              <span className="font-medium">{customerData.email}</span>
            </div>
            <div className="flex justify-between">
              <span>Phone:</span>
              <span className="font-medium">{customerData.phoneNumber}</span>
            </div>
            <div className="flex justify-between">
              <span>Wallet Balance:</span>
              <span className="font-medium">{formatCurrency(customerData.walletBalance)}</span>
            </div>
            <div className="flex justify-between">
              <span>Employment:</span>
              <span className="font-medium capitalize">{customerData.employment}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanDashboardModal;
